import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import EmailSuggestionModal from '../components/EmailSuggestionModal';
import LeadSuggestionCard from '../components/LeadSuggestionCard';
import {
  LeadSuggestion,
  OrganizationLeadPreferences,
  RejectionReason,
  leadSuggestionService,
  terminalReasonCopy,
} from '../services/leadSuggestionService';

const DailyLeadSuggestions: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState<OrganizationLeadPreferences | null>(null);
  const [suggestions, setSuggestions] = useState<LeadSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState<'score' | 'created'>('score');
  const [activeSuggestion, setActiveSuggestion] = useState<LeadSuggestion | null>(null);

  const isAdmin = user?.role === 'admin';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [prefsData, suggestionData] = await Promise.all([
        leadSuggestionService.getLeadPreferences(),
        leadSuggestionService.list({
          status: filterStatus || undefined,
          sort: sortBy === 'created' ? undefined : sortBy,
        }),
      ]);
      setPrefs(prefsData);
      setSuggestions(Array.isArray(suggestionData) ? suggestionData : []);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to load lead suggestions', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, sortBy, showToast]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleConvert = async (id: string) => {
    try {
      await leadSuggestionService.convertToLead(id);
      showToast('Lead added to your pipeline', 'success');
      await loadAll();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to add lead', 'error');
    }
  };

  const handleReject = async (id: string, reason?: RejectionReason) => {
    try {
      await leadSuggestionService.rejectSuggestion(id, reason);
      await loadAll();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed', 'error');
    }
  };

  const suggestedToday = suggestions.filter((s) => s.status === 'suggested').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-fg">Today's Leads</h1>
          <p className="text-sm text-fg-muted mt-1">
            {suggestedToday > 0
              ? `${suggestedToday} lead${suggestedToday !== 1 ? 's' : ''} ready for review`
              : 'Your daily leads refresh each morning automatically'}
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/tenant?tab=settings"
            className="px-4 py-2 text-sm rounded border border-border text-fg hover:bg-surface-muted"
          >
            Preferences
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-sm">
          <span className="block mb-1 text-fg-muted">View</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded border border-border bg-app px-3 py-2 text-fg"
          >
            <option value="">New leads</option>
            <option value="accepted">Saved</option>
            <option value="converted">Added to pipeline</option>
            <option value="rejected">Passed on</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block mb-1 text-fg-muted">Sort</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'score' | 'created')}
            className="rounded border border-border bg-app px-3 py-2 text-fg"
          >
            <option value="score">Best match first</option>
            <option value="created">Newest first</option>
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-fg-muted">Loading your leads...</p>
      ) : suggestions.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-6 text-center space-y-2">
          {prefs && !prefs.preferences_complete ? (
            <>
              <p className="text-sm text-fg-muted">Set up your preferences to start receiving daily leads.</p>
              <Link
                to={isAdmin ? '/tenant?tab=settings' : '/onboarding/lead-preferences'}
                className="text-sm text-primary hover:underline"
              >
                Set up preferences →
              </Link>
            </>
          ) : (
            <p className="text-sm text-fg-muted">
              {filterStatus
                ? 'No leads in this view.'
                : terminalReasonCopy(
                    typeof window !== 'undefined'
                      ? window.sessionStorage.getItem('lp_last_terminal_reason')
                      : null,
                  ) || 'Your leads for today will appear here each morning.'}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((suggestion) => (
            <LeadSuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              onConvert={handleConvert}
              onReject={handleReject}
              onGenerateEmail={setActiveSuggestion}
            />
          ))}
        </div>
      )}

      <EmailSuggestionModal
        open={Boolean(activeSuggestion)}
        suggestion={activeSuggestion}
        onClose={() => setActiveSuggestion(null)}
      />
    </div>
  );
};

export default DailyLeadSuggestions;
